/**
 * Hideaway v6 — Router configuration.
 *
 * All tool routes are accessible but wrapped with AuthGate
 * at the component level (each route controls its own gate).
 *
 * Routes are lazy-loaded to keep the initial bundle small.
 */
import { createBrowserRouter } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Layout } from "./components/Layout";
import { Home } from "./routes/Home";

// Lazy-loaded route components (code-split per page)
const Hide = lazy(() => import("./routes/Hide").then((m) => ({ default: m.Hide })));
const Reveal = lazy(() => import("./routes/Reveal").then((m) => ({ default: m.Reveal })));
const Scan = lazy(() => import("./routes/Scan").then((m) => ({ default: m.Scan })));
const Analyze = lazy(() => import("./routes/Analyze").then((m) => ({ default: m.Analyze })));
const Learn = lazy(() => import("./routes/Learn").then((m) => ({ default: m.Learn })));
const History = lazy(() => import("./routes/History").then((m) => ({ default: m.History })));
const Inject = lazy(() => import("./routes/Inject").then((m) => ({ default: m.Inject })));
const Privacy = lazy(() => import("./routes/Privacy").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("./routes/Terms").then((m) => ({ default: m.Terms })));
const Settings = lazy(() => import("./routes/Settings").then((m) => ({ default: m.Settings })));
const Login = lazy(() => import("./routes/Login").then((m) => ({ default: m.Login })));
const Labs = lazy(() => import("./routes/Labs").then((m) => ({ default: m.Labs })));
const Providers = lazy(() => import("./routes/Providers").then((m) => ({ default: m.Providers })));
const Wiki = lazy(() => import("./routes/Wiki").then((m) => ({ default: m.Wiki })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center py-32">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/hide", element: <LazyPage><Hide /></LazyPage> },
      { path: "/reveal", element: <LazyPage><Reveal /></LazyPage> },
      { path: "/scan", element: <LazyPage><Scan /></LazyPage> },
      { path: "/analyze", element: <LazyPage><Analyze /></LazyPage> },
      { path: "/inject", element: <LazyPage><Inject /></LazyPage> },
      { path: "/labs", element: <LazyPage><Labs /></LazyPage> },
      { path: "/labs/wiki", element: <LazyPage><Wiki /></LazyPage> },
      { path: "/providers", element: <LazyPage><Providers /></LazyPage> },
      { path: "/history", element: <LazyPage><History /></LazyPage> },
      { path: "/learn", element: <LazyPage><Learn /></LazyPage> },
      { path: "/privacy", element: <LazyPage><Privacy /></LazyPage> },
      { path: "/terms", element: <LazyPage><Terms /></LazyPage> },
      { path: "/settings", element: <LazyPage><Settings /></LazyPage> },
      { path: "/login", element: <LazyPage><Login /></LazyPage> },
      // Text Steg moved to Cryptex — redirect legacy URLs
      {
        path: "/text-steg",
        loader: () => {
          window.location.href = "https://cryptex.m4xx.cfd?ref=hideaway";
          return null;
        },
        element: (
          <div className="flex items-center justify-center py-32 text-sm text-muted-foreground">
            Redirecting to Cryptex…
          </div>
        ),
      },
    ],
  },
]);
